import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import type { ReleaseListRow } from 'fym-shared/src/music24/releases';
import { TypedMemo } from '../../../../common/types';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { SMLLink } from '../../../components/SMLLink/SMLLink';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { daysUntilRelease } from '../releaseOrder';
import { formatReleaseWhen } from '../releaseWhen';
import { ReleaseArtwork } from '../ReleaseArtwork';
import { releaseNextStepText } from './releaseNextStepText';
import { releaseListMessages } from './releaseListMessages';
import { useQuietOpen } from './useQuietOpen';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './ReleasesList.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface QuietEntry {
  item: CatalogItem;
  row: ReleaseListRow;
}

interface QuietReleasesProps {
  entries: QuietEntry[];
  now: Date;
  /** A search shows the matches open. */
  forceOpen: boolean;
}

/**
 * The Quiet group (design a1, a3): closed, a stack of covers and "12 quiet songs"; open, one short
 * row per song whose next step links to the right tab of the release.
 */
const QuietReleasesComponent: React.FC<QuietReleasesProps> = ({ entries, now, forceOpen }) => {
  const intl = useIntl();
  const { isOpen, toggle } = useQuietOpen();
  const open = forceOpen || isOpen;
  const stack = useMemo(() => entries.slice(0, 4), [entries]);

  return (
    <>
      <div className={styles.sect}>
        <h4 className={styles.sectTitle}>{intl.formatMessage(releaseListMessages.quiet)}</h4>
        <span className={styles.sectSub}>
          {open
            ? intl.formatMessage(releaseListMessages.quietSongs, { count: entries.length })
            : intl.formatMessage(releaseListMessages.quietSub)}
        </span>
        {open && !forceOpen && (
          <span className={styles.sectEnd}>
            <button type="button" className={styles.textLink} onClick={toggle} aria-expanded>
              {intl.formatMessage(releaseListMessages.hide)}
            </button>
          </span>
        )}
      </div>
      {!open && (
        <div className={styles.more}>
          <span className={styles.stack} aria-hidden="true">
            {stack.map(({ item }) => (
              <ReleaseArtwork key={item.uuid} item={item} className={styles.stackCover} />
            ))}
          </span>
          <span className={styles.grow}>
            {intl.formatMessage(releaseListMessages.quietCount, { count: entries.length })}
          </span>
          <SMLButton onClick={toggle} ariaExpanded={false} icon={<FontAwesomeIcon icon={faChevronDown} />}>
            {intl.formatMessage(releaseListMessages.show)}
          </SMLButton>
        </div>
      )}
      {open &&
        entries.map(({ item, row }) => {
          const step = releaseNextStepText(intl, row.nextStep);
          const when = formatReleaseWhen(intl, item.releaseDate, daysUntilRelease(item.releaseDate, now));
          return (
            <SMLLink
              key={item.uuid}
              to={SMLRoutes.promoteRelease}
              params={{ catalogItemUuid: item.uuid, tab: row.nextStep.tab }}
              className={styles.quietRow}
            >
              <ReleaseArtwork item={item} className={`${styles.cover} ${styles.quietCover}`} />
              <span className={styles.grow}>
                <b className={styles.nextTitle}>{item.title}</b>
                <small className={styles.quietLine}>
                  {intl.formatMessage(releaseListMessages.quietLine, {
                    when,
                    assets: row.assets.count,
                    posts: row.posts.done + row.posts.planned,
                  })}
                </small>
              </span>
              <span className={styles.textLink}>{step.title}</span>
            </SMLLink>
          );
        })}
    </>
  );
};

export const QuietReleases = TypedMemo(QuietReleasesComponent);
